import { useState } from 'react';
import { Field } from './ui.jsx';
import { validatePhone, phoneDigits, PHONE_DIGITS } from '../lib/validation.js';

/**
 * Phone input, mirroring `server/src/utils.js` `validatePhone` — the server copy
 * stays the authoritative check.
 *
 * The platform serves Kerala only, so this is a fixed 10-digit field: the box
 * holds nothing but digits and physically cannot take an eleventh. A pasted
 * +91 / 0 prefix is stripped rather than rejected, because that is how a number
 * arrives from a phone's contact list.
 */
export default function PhoneField({ id = 'phone', label = 'Phone', value, onChange, required }) {
  const [touched, setTouched] = useState(false);
  const result = validatePhone(value);
  const digits = phoneDigits(value);

  // A full-length number that is still invalid (wrong leading digit) can only
  // be fixed by editing, so report it without waiting for blur.
  const settled = digits.length >= PHONE_DIGITS;
  const showError = (touched || settled) && !!value && !result.ok;

  /** Keeps the field to digits only, capped at 10, for typing and pasting alike. */
  const handleChange = (raw) => onChange(phoneDigits(raw).slice(0, PHONE_DIGITS));

  return (
    <Field
      label={label}
      id={id}
      required={required}
      error={showError ? result.error : undefined}
      hint={
        !showError
          ? digits.length > 0
            ? `${digits.length} of ${PHONE_DIGITS} digits`
            : `${PHONE_DIGITS}-digit Indian mobile number`
          : undefined
      }
    >
      <input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder="9847012345"
        maxLength={PHONE_DIGITS}
        pattern="[6-9][0-9]{9}"
        aria-invalid={showError || undefined}
        required={required}
      />
    </Field>
  );
}
